import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, ArrowRight, Search, Cpu, Rocket, Palette, PenLine, Search as SeoIcon,
  Smartphone, Zap, MessageSquareQuote, LayoutTemplate, MousePointerClick, Star, MapPin
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const features = [
  { icon: Palette, title: "AI Branding", desc: "A unique color palette, typography and visual identity generated from your business — never a template." },
  { icon: PenLine, title: "AI Copywriting", desc: "Persuasive, local copy written from your reviews and customer psychology." },
  { icon: SeoIcon, title: "SEO Optimization", desc: "Meta tags, schema, local keywords and sitemaps built in for real search visibility." },
  { icon: Smartphone, title: "Mobile Responsive", desc: "Thumb-friendly layouts, click-to-call and sticky CTAs on every device." },
  { icon: Zap, title: "Fast Hosting", desc: "Blazing-fast pages engineered for 90+ Lighthouse scores." },
  { icon: MessageSquareQuote, title: "Review Analysis", desc: "We read your reviews to find what customers love — then put it front and center." },
  { icon: LayoutTemplate, title: "Custom Design", desc: "Two businesses never look alike. Layouts adapt to your industry." },
  { icon: MousePointerClick, title: "One-Click Publishing", desc: "Go live on a MapLift domain or connect your own in seconds." },
];

const steps = [
  { icon: Search, k: "01", title: "Paste your Google Maps URL", desc: "Drop the link to your business on Google Maps. That's all we need to begin." },
  { icon: Cpu, k: "02", title: "AI learns your business", desc: "We analyze your details, reviews and category to build a brand and strategy." },
  { icon: Rocket, k: "03", title: "Launch your website", desc: "Review, tweak anything, and publish a professional site in minutes." },
];

export default function Landing() {
  const [url, setUrl] = useState("");
  const nav = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    const v = url.trim();
    if (!v) { toast.error("Paste your Google Maps link to begin."); return; }
    nav(`/create?url=${encodeURIComponent(v)}`);
  };

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 ml-grid-bg [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 pt-20 pb-24 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .6 }}
            className="inline-flex items-center gap-2 border border-border rounded-full px-4 py-1.5 mb-8 bg-white">
            <Sparkles size={14} className="text-sky-500" />
            <span className="text-xs font-medium text-[#52525b] font-body">AI-powered · Publish in minutes</span>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7, delay: .05 }}
            className="font-heading font-bold text-4xl sm:text-6xl tracking-tighter leading-[1.05] text-[#09090b]">
            Turn your Google Maps listing into a<br className="hidden sm:block" /> <span className="ml-ai-text">professional website</span> in minutes.
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7, delay: .12 }}
            className="font-body text-lg text-[#52525b] mt-6 max-w-2xl mx-auto">
            One URL. Five minutes. One beautiful website. No coding, no designers, no templates.
          </motion.p>

          <motion.form onSubmit={submit} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7, delay: .18 }}
            className="mt-10 max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 bg-white p-2 rounded-2xl sm:rounded-full border border-border shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)]">
              <div className="flex items-center gap-3 flex-1 px-4">
                <MapPin size={20} className="text-[#a1a1aa] shrink-0" />
                <input
                  data-testid="maps-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="Paste your Google Maps URL here..."
                  className="w-full bg-transparent py-3 text-base outline-none font-body placeholder:text-[#a1a1aa]"
                />
              </div>
              <button data-testid="lift-business-btn" type="submit"
                className="ml-ai-btn rounded-full px-7 py-3.5 font-semibold text-sm inline-flex items-center justify-center gap-2 font-body">
                Lift My Business <ArrowRight size={16} />
              </button>
            </div>
          </motion.form>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: .7, delay: .3 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#52525b] font-body">
            {["Mobile Optimized", "SEO Ready", "AI Generated", "Publish in Minutes"].map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5"><Star size={12} className="text-sky-500" fill="#0ea5e9" /> {b}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 border-t border-border bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="ml-label mb-3">How it works</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090b] max-w-xl">Three steps from listing to launch.</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {steps.map((s, i) => (
              <motion.div key={s.k} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * .1 }}
                className="bg-white border border-border rounded-2xl p-8">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#09090b] flex items-center justify-center"><s.icon size={20} className="text-sky-400" /></div>
                  <span className="font-mono-ml text-sm text-[#a1a1aa]">{s.k}</span>
                </div>
                <h3 className="font-heading font-semibold text-xl text-[#09090b] mt-6">{s.title}</h3>
                <p className="font-body text-[#52525b] mt-3 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Before / After */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="ml-label mb-3">The transformation</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090b] max-w-2xl">Your business already exists. Now give it a website.</h2>
          <div className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-8 mt-14">
            <div className="bg-[#fafafa] border border-border rounded-2xl p-6">
              <div className="ml-label mb-4">Before · Google Business Profile</div>
              <div className="bg-white border border-border rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#e4e4e7]" />
                  <div>
                    <div className="h-3 w-32 bg-[#d4d4d8] rounded" />
                    <div className="h-2.5 w-24 bg-[#e4e4e7] rounded mt-2" />
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-4">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="text-amber-400" fill="#fbbf24" />)}</div>
                <div className="h-2.5 w-full bg-[#e4e4e7] rounded mt-4" />
                <div className="h-2.5 w-3/4 bg-[#e4e4e7] rounded mt-2" />
              </div>
            </div>
            <motion.div initial={{ x: -6 }} animate={{ x: 6 }} transition={{ repeat: Infinity, repeatType: "reverse", duration: 1 }}
              className="hidden md:flex w-12 h-12 rounded-full ml-ai-btn items-center justify-center mx-auto">
              <ArrowRight size={20} />
            </motion.div>
            <div className="rounded-2xl p-[2px] bg-gradient-to-r from-sky-400 to-blue-600">
              <div className="bg-white rounded-2xl p-6 h-full">
                <div className="ml-label mb-4">After · MapLift Website</div>
                <img src="https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg?auto=compress&cs=tinysrgb&w=900" alt="Modern website preview" className="w-full h-56 object-cover rounded-xl" />
                <div className="flex gap-2 mt-4">
                  <div className="h-8 w-28 rounded-full bg-[#09090b]" />
                  <div className="h-8 w-24 rounded-full border border-border" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 border-t border-border bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="ml-label mb-3">Everything included</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090b] max-w-2xl">A full agency, distilled into one AI.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {features.map((f, i) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * .06 }}
                className="bg-white border border-border rounded-2xl p-6 hover:shadow-[0_8px_24px_-4px_rgba(0,0,0,0.08)] transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-[#f4f4f5] flex items-center justify-center"><f.icon size={18} className="text-[#09090b]" /></div>
                <h3 className="font-heading font-semibold text-lg text-[#09090b] mt-5">{f.title}</h3>
                <p className="font-body text-sm text-[#52525b] mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-[#09090b]">Your website is one link away.</h2>
          <p className="font-body text-lg text-[#52525b] mt-5">Paste your Google Maps URL and watch MapLift build it for you.</p>
          <button data-testid="cta-start-btn" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="ml-ai-btn rounded-full px-8 py-4 font-semibold text-sm mt-8 inline-flex items-center gap-2 font-body">
            Lift My Business <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
